/**
 * Multiplayer Highway — client runtime (seeded fallback + server snapshot render).
 */
import type { MpHighwayStateWire } from "../../../lib/multiplayer/protocol";
import {
  MP_HIGHWAY_LAYOUT_SCALE,
  MP_REF_H,
  MP_REF_W,
  MP_SCORE_PER_SEC,
  mpCreateRng,
} from "../../../lib/multiplayer/normal-sim";
import {
  applyHighwaySnapshot,
  drawHighwayLayerRefMapped,
  highwayCollides,
  highwayCursorOffScreen,
  highwayCursorOffTrack,
  highwayCursorScreenPos,
  highwayWorldToScreen,
  resetHighwayState,
  setHighwayRandom,
  updateHighway,
  type HighwayObstacle,
} from "../highway-mode";

let sessionActive = false;
let visualScore = 0;

export function resetMpHighwayClient(): void {
  sessionActive = false;
  visualScore = 0;
}

export function beginMpHighwayClient(seed: number): void {
  const s = seed >>> 0;
  setHighwayRandom(mpCreateRng(s));
  resetHighwayState(MP_REF_W, MP_REF_H);
  visualScore = 0;
  sessionActive = true;
  updateHighway(0, 0, MP_REF_W, MP_REF_H, MP_HIGHWAY_LAYOUT_SCALE);
}

export function isMpHighwayClientActive(): boolean {
  return sessionActive;
}

export function mpHighwayWorldSpawn(): { x: number; y: number } {
  return { x: MP_REF_W / 2, y: 0.12 * MP_REF_H };
}

export function tickMpHighwayClient(
  dt: number,
  serverScore?: number,
  serverHighway?: MpHighwayStateWire,
): void {
  if (!sessionActive) return;

  if (Number.isFinite(serverScore)) {
    visualScore = Math.max(visualScore, serverScore!);
  } else {
    visualScore += MP_SCORE_PER_SEC * dt;
  }

  if (serverHighway) {
    applyHighwaySnapshot({
      cameraY: serverHighway.cameraY,
      obstacles: serverHighway.obstacles as HighwayObstacle[],
      nextSpawnWorldY: serverHighway.nextSpawnWorldY,
    });
  } else {
    updateHighway(dt, visualScore, MP_REF_W, MP_REF_H, MP_HIGHWAY_LAYOUT_SCALE);
  }
}

export function drawMpHighwayClient(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  accentHex: string,
  nowMs: number,
): void {
  if (!sessionActive || cw <= 0 || ch <= 0) return;
  drawHighwayLayerRefMapped(
    ctx,
    cw,
    ch,
    MP_REF_W,
    MP_REF_H,
    MP_HIGHWAY_LAYOUT_SCALE,
    accentHex,
    nowMs,
  );
}

export function mpHighwayClientScore(): number {
  return visualScore;
}

export function mpHighwayCursorOnCanvas(
  cw: number,
  ch: number,
  worldX: number,
  worldY: number,
): { x: number; y: number } {
  return highwayWorldToScreen(worldX, worldY, cw, ch, MP_REF_W, MP_REF_H);
}

export function mpHighwayCheckCollision(
  worldX: number,
  worldY: number,
  cursorRadiusPx: number,
  cw: number,
  ch: number,
): boolean {
  if (!sessionActive || cw <= 0 || ch <= 0) return false;
  const sx = cw / MP_REF_W;
  const sy = ch / MP_REF_H;
  const refCr = cursorRadiusPx / Math.min(sx, sy);
  const scr = highwayCursorScreenPos(worldX, worldY, MP_REF_H);
  if (highwayCursorOffScreen(scr.x, scr.y, refCr, MP_REF_W, MP_REF_H)) return true;
  if (highwayCursorOffTrack(scr.x, refCr, MP_REF_W)) return true;
  if (highwayCollides(scr.x, scr.y, refCr, MP_REF_H)) return true;
  return false;
}
